import { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { adminApi, assetUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, GripVertical, ArrowUp, ArrowDown, Copy, Trash2, Pencil, Eye, Upload, Image as ImageIcon,
} from "lucide-react";

const ICON_OPTIONS = ["check", "shield", "lock", "fingerprint", "map", "map-pin", "users", "calendar", "megaphone", "qr-code", "activity", "file-text", "scale"];

const EMPTY = {
  label: "", headline: "", subheadline: "", description: "", image_alt: "",
  image_desktop: null, image_mobile: null,
  ctas: [
    { text: "", url: "", enabled: true, external: false, new_tab: false },
    { text: "", url: "", enabled: false, external: false, new_tab: false },
    { text: "", url: "", enabled: false, external: false, new_tab: false },
  ],
  feature_points: [],
  text_align: "left", overlay_strength: "strong", active: true,
  starts_at: null, ends_at: null,
};

const toLocalInput = (iso) => (iso ? iso.slice(0, 16) : "");
const fromLocalInput = (v) => (v ? new Date(v).toISOString() : null);

export default function Website() {
  const [heroes, setHeroes] = useState([]);
  const [config, setConfig] = useState({ auto_rotate: true, duration_seconds: 8 });
  const [editing, setEditing] = useState(null); // {id?|null, ...fields}
  const [previewing, setPreviewing] = useState(null);
  const [busy, setBusy] = useState(false);
  const dragIndex = useRef(null);

  const load = () => {
    adminApi.get("/admin/heroes").then((r) => setHeroes(r.data)).catch(() => {});
    adminApi.get("/admin/heroes/config").then((r) => setConfig(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const saveConfig = async (next) => {
    setConfig(next);
    try {
      await adminApi.patch("/admin/heroes/config", next);
      toast.success("Carousel settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save settings");
    }
  };

  const openEdit = (hero) => {
    setEditing(hero ? {
      ...EMPTY, ...hero,
      subheadline: hero.subheadline || "", description: hero.description || "",
      starts_at: toLocalInput(hero.starts_at), ends_at: toLocalInput(hero.ends_at),
      ctas: (hero.ctas || []).concat(Array(3).fill(null)).slice(0, 3).map((c) => c || { text: "", url: "", enabled: false, external: false, new_tab: false }),
      feature_points: hero.feature_points || [],
    } : { ...EMPTY, id: null, starts_at: "", ends_at: "" });
  };

  const setField = (k, v) => setEditing((e) => ({ ...e, [k]: v }));
  const setCta = (i, k, v) => setEditing((e) => {
    const ctas = e.ctas.map((c, ci) => (ci === i ? { ...c, [k]: v } : c));
    return { ...e, ctas };
  });
  const setPoint = (i, k, v) => setEditing((e) => {
    const feature_points = e.feature_points.map((p, pi) => (pi === i ? { ...p, [k]: v } : p));
    return { ...e, feature_points };
  });

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        label: editing.label, headline: editing.headline,
        subheadline: editing.subheadline || null, description: editing.description || null,
        image_desktop: editing.image_desktop, image_mobile: editing.image_mobile,
        image_alt: editing.image_alt || "",
        ctas: editing.ctas, feature_points: editing.feature_points.filter((p) => p.text),
        text_align: editing.text_align, overlay_strength: editing.overlay_strength,
        active: editing.active,
        starts_at: fromLocalInput(editing.starts_at), ends_at: fromLocalInput(editing.ends_at),
      };
      if (editing.id) {
        await adminApi.patch(`/admin/heroes/${editing.id}`, payload);
        toast.success("Hero updated");
      } else {
        await adminApi.post("/admin/heroes", payload);
        toast.success("Hero created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (hero, active) => {
    try {
      await adminApi.post(`/admin/heroes/${hero.id}/toggle`, { active });
      toast.success(active ? "Hero activated" : "Hero deactivated");
      load();
    } catch (e) {
      toast.error("Toggle failed");
    }
  };

  const duplicate = async (hero) => {
    try {
      await adminApi.post(`/admin/heroes/${hero.id}/duplicate`);
      toast.success("Hero duplicated (inactive copy created)");
      load();
    } catch (e) {
      toast.error("Duplicate failed");
    }
  };

  const remove = async (hero) => {
    try {
      await adminApi.delete(`/admin/heroes/${hero.id}`);
      toast.success("Hero deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const persistOrder = async (list) => {
    setHeroes(list);
    try {
      await adminApi.post("/admin/heroes/reorder", { ordered_ids: list.map((h) => h.id) });
    } catch {
      toast.error("Reorder failed");
      load();
    }
  };

  const move = (index, dir) => {
    const next = [...heroes];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    persistOrder(next);
  };

  const onDrop = (index) => {
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from === null || from === index) return;
    const next = [...heroes];
    const [item] = next.splice(from, 1);
    next.splice(index, 0, item);
    persistOrder(next);
  };

  const uploadImage = async (heroId, kind, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await adminApi.post(`/admin/heroes/${heroId}/image?kind=${kind}`, fd);
      toast.success(`${kind} image uploaded and optimized (WebP, ${r.data.width}px)`);
      load();
      setEditing((e) => (e && e.id === heroId ? { ...e, [kind === "desktop" ? "image_desktop" : "image_mobile"]: r.data.url } : e));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    }
  };

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
        <h1 className="font-heading font-bold text-2xl text-foreground" data-testid="admin-website-title">Website · Homepage heroes</h1>
        <Button onClick={() => openEdit(null)} data-testid="hero-create-button"><Plus className="h-4 w-4 mr-2" /> New hero</Button>
      </div>
      <p className="text-xs text-muted-foreground mb-6">
        Full control of the rotating homepage hero — no code changes needed. Drag rows (or use arrows) to reorder.
        Scheduled heroes only appear between their start and end dates.
      </p>

      <Card className="mb-6"><CardContent className="p-5 flex flex-wrap items-center gap-6">
        <div className="flex items-center gap-3">
          <Switch checked={config.auto_rotate} onCheckedChange={(v) => saveConfig({ ...config, auto_rotate: v })} data-testid="hero-config-autorotate" />
          <span className="text-sm font-medium">Auto-rotation</span>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="rot-secs" className="text-sm">Rotate every</Label>
          <Input id="rot-secs" type="number" min={4} max={30} className="w-20" value={config.duration_seconds}
                 onChange={(e) => setConfig({ ...config, duration_seconds: parseInt(e.target.value || "8", 10) })}
                 onBlur={() => saveConfig({ ...config, duration_seconds: Math.min(30, Math.max(4, config.duration_seconds || 8)) })}
                 data-testid="hero-config-duration" />
          <span className="text-sm text-muted-foreground">seconds (7–10 recommended)</span>
        </div>
      </CardContent></Card>

      <div className="space-y-3" data-testid="hero-list">
        {heroes.map((hero, i) => (
          <Card
            key={hero.id}
            draggable
            onDragStart={() => { dragIndex.current = i; }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDrop(i)}
            className="cursor-default"
            data-testid={`hero-row-${i}`}
          >
            <CardContent className="p-4 flex items-center gap-4 flex-wrap">
              <span className="cursor-grab text-slate-400" title="Drag to reorder"><GripVertical className="h-5 w-5" /></span>
              <div className="flex flex-col gap-1">
                <button type="button" onClick={() => move(i, -1)} aria-label="Move up" className="text-slate-400 hover:text-slate-700" data-testid={`hero-move-up-${i}`}><ArrowUp className="h-4 w-4" /></button>
                <button type="button" onClick={() => move(i, 1)} aria-label="Move down" className="text-slate-400 hover:text-slate-700" data-testid={`hero-move-down-${i}`}><ArrowDown className="h-4 w-4" /></button>
              </div>
              {hero.image_desktop ? (
                <img src={assetUrl(hero.image_desktop)} alt="" className="h-14 w-24 object-cover rounded-md border border-slate-200" />
              ) : (
                <div className="h-14 w-24 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center"><ImageIcon className="h-5 w-5 text-slate-400" /></div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-bold tracking-wider text-accent uppercase">{hero.label}</p>
                <p className="font-heading font-semibold text-sm text-foreground truncate">{hero.headline}</p>
                <div className="flex gap-1.5 mt-1 flex-wrap">
                  {(hero.starts_at || hero.ends_at) && (
                    <Badge variant="outline" className="text-[10px]">
                      {hero.starts_at ? `from ${hero.starts_at.slice(0, 10)}` : ""} {hero.ends_at ? `until ${hero.ends_at.slice(0, 10)}` : ""}
                    </Badge>
                  )}
                  <Badge variant="outline" className="text-[10px]">{(hero.ctas || []).filter((c) => c.enabled && c.text).length} CTAs</Badge>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={hero.active} onCheckedChange={(v) => toggle(hero, v)} aria-label="Active" data-testid={`hero-active-${i}`} />
                <Button size="sm" variant="outline" onClick={() => setPreviewing(hero)} data-testid={`hero-preview-${i}`}><Eye className="h-3.5 w-3.5 mr-1" /> Preview</Button>
                <Button size="sm" variant="outline" onClick={() => openEdit(hero)} data-testid={`hero-edit-${i}`}><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => duplicate(hero)} aria-label="Duplicate" data-testid={`hero-duplicate-${i}`}><Copy className="h-4 w-4" /></Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="ghost" className="text-rose-600" aria-label="Delete" data-testid={`hero-delete-${i}`}><Trash2 className="h-4 w-4" /></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-white">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete this hero?</AlertDialogTitle>
                      <AlertDialogDescription>“{hero.headline}” will be removed from the homepage carousel. This is recorded in the audit log.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(hero)} data-testid="hero-delete-confirm">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* EDIT / CREATE DIALOG */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-white max-w-3xl max-h-[88vh] overflow-y-auto">
          {editing && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{editing.id ? "Edit hero" : "New hero"}</DialogTitle></DialogHeader>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Label (small eyebrow text)</Label>
                  <Input className="mt-1.5" value={editing.label} onChange={(e) => setField("label", e.target.value)} data-testid="hero-form-label" />
                </div>
                <div>
                  <Label>Text alignment</Label>
                  <Select value={editing.text_align} onValueChange={(v) => setField("text_align", v)}>
                    <SelectTrigger className="mt-1.5" data-testid="hero-form-align"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="left">Left</SelectItem>
                      <SelectItem value="center">Center</SelectItem>
                      <SelectItem value="right">Right</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Headline</Label>
                <Input className="mt-1.5" value={editing.headline} onChange={(e) => setField("headline", e.target.value)} data-testid="hero-form-headline" />
              </div>
              <div>
                <Label>Sub-headline (optional)</Label>
                <Input className="mt-1.5" value={editing.subheadline} onChange={(e) => setField("subheadline", e.target.value)} data-testid="hero-form-subheadline" />
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Textarea className="mt-1.5" rows={3} value={editing.description} onChange={(e) => setField("description", e.target.value)} data-testid="hero-form-description" />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Overlay strength</Label>
                  <Select value={editing.overlay_strength} onValueChange={(v) => setField("overlay_strength", v)}>
                    <SelectTrigger className="mt-1.5" data-testid="hero-form-overlay"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="light">Light</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="strong">Strong</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Image alt text</Label>
                  <Input className="mt-1.5" value={editing.image_alt} onChange={(e) => setField("image_alt", e.target.value)} data-testid="hero-form-alt" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="rounded-lg border border-slate-200 p-3.5">
                  <p className="text-xs font-semibold mb-2">Desktop image (16:9 or wider)</p>
                  {editing.image_desktop && <img src={assetUrl(editing.image_desktop)} alt="" className="h-20 w-full object-cover rounded mb-2" />}
                  {editing.id ? (
                    <label className="cursor-pointer inline-flex items-center text-xs font-medium text-primary hover:underline">
                      <Upload className="h-3.5 w-3.5 mr-1" /> Upload / replace
                      <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                             onChange={(e) => e.target.files[0] && uploadImage(editing.id, "desktop", e.target.files[0])}
                             data-testid="hero-upload-desktop" />
                    </label>
                  ) : (
                    <p className="text-[11px] text-muted-foreground">Save the hero first, then upload images (auto-optimized to WebP). You can also paste an image URL below.</p>
                  )}
                  <Input className="mt-2 text-xs" placeholder="…or paste an image URL" value={editing.image_desktop || ""} onChange={(e) => setField("image_desktop", e.target.value || null)} data-testid="hero-form-desktop-url" />
                </div>
                <div className="rounded-lg border border-slate-200 p-3.5">
                  <p className="text-xs font-semibold mb-2">Mobile image (portrait crop, optional)</p>
                  {editing.image_mobile && <img src={assetUrl(editing.image_mobile)} alt="" className="h-20 w-full object-cover rounded mb-2" />}
                  {editing.id && (
                    <label className="cursor-pointer inline-flex items-center text-xs font-medium text-primary hover:underline">
                      <Upload className="h-3.5 w-3.5 mr-1" /> Upload / replace
                      <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                             onChange={(e) => e.target.files[0] && uploadImage(editing.id, "mobile", e.target.files[0])}
                             data-testid="hero-upload-mobile" />
                    </label>
                  )}
                  <Input className="mt-2 text-xs" placeholder="…or paste an image URL" value={editing.image_mobile || ""} onChange={(e) => setField("image_mobile", e.target.value || null)} data-testid="hero-form-mobile-url" />
                </div>
              </div>

              <div>
                <p className="text-sm font-semibold mb-2">Call-to-action buttons</p>
                <div className="space-y-3">
                  {editing.ctas.map((cta, i) => (
                    <div key={i} className="rounded-lg border border-slate-200 p-3.5">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-bold text-slate-500">CTA #{i + 1} {i === 0 ? "(primary)" : i === 1 ? "(secondary)" : "(text link)"}</p>
                        <label className="flex items-center gap-2 text-xs cursor-pointer">
                          <Checkbox checked={cta.enabled} onCheckedChange={(v) => setCta(i, "enabled", !!v)} data-testid={`hero-cta-${i}-enabled`} /> Enabled
                        </label>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <Input placeholder="Button text" value={cta.text} onChange={(e) => setCta(i, "text", e.target.value)} data-testid={`hero-cta-${i}-text`} />
                        <Input placeholder="/internal-path or https://…" value={cta.url} onChange={(e) => setCta(i, "url", e.target.value)} data-testid={`hero-cta-${i}-url`} />
                      </div>
                      <div className="flex gap-5 mt-2.5">
                        <label className="flex items-center gap-2 text-xs cursor-pointer">
                          <Checkbox checked={cta.external} onCheckedChange={(v) => setCta(i, "external", !!v)} data-testid={`hero-cta-${i}-external`} /> External link
                        </label>
                        <label className="flex items-center gap-2 text-xs cursor-pointer">
                          <Checkbox checked={cta.new_tab} onCheckedChange={(v) => setCta(i, "new_tab", !!v)} data-testid={`hero-cta-${i}-newtab`} /> Open in new tab
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold">Trust / feature points</p>
                  <Button size="sm" variant="outline" onClick={() => setField("feature_points", [...editing.feature_points, { icon: "check", text: "" }])} data-testid="hero-add-point">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add point
                  </Button>
                </div>
                <div className="space-y-2">
                  {editing.feature_points.map((p, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <Select value={p.icon} onValueChange={(v) => setPoint(i, "icon", v)}>
                        <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {ICON_OPTIONS.map((ic) => <SelectItem key={ic} value={ic}>{ic}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Input value={p.text} onChange={(e) => setPoint(i, "text", e.target.value)} placeholder="Point text" data-testid={`hero-point-${i}-text`} />
                      <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => setField("feature_points", editing.feature_points.filter((_, pi) => pi !== i))} aria-label="Remove point">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Publish from (optional)</Label>
                  <Input type="datetime-local" className="mt-1.5" value={editing.starts_at || ""} onChange={(e) => setField("starts_at", e.target.value)} data-testid="hero-form-starts" />
                </div>
                <div>
                  <Label>Publish until (optional)</Label>
                  <Input type="datetime-local" className="mt-1.5" value={editing.ends_at || ""} onChange={(e) => setField("ends_at", e.target.value)} data-testid="hero-form-ends" />
                </div>
              </div>

              <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3.5">
                <div>
                  <p className="text-sm font-medium">Active</p>
                  <p className="text-xs text-muted-foreground">Inactive heroes never appear, regardless of schedule</p>
                </div>
                <Switch checked={editing.active} onCheckedChange={(v) => setField("active", v)} data-testid="hero-form-active" />
              </div>

              <div className="flex gap-2">
                <Button onClick={save} disabled={busy || !editing.label || !editing.headline} data-testid="hero-form-save">
                  {busy ? "Saving…" : editing.id ? "Save changes" : "Create hero"}
                </Button>
                <Button variant="outline" onClick={() => setPreviewing({ ...editing, starts_at: null, ends_at: null })} data-testid="hero-form-preview">
                  <Eye className="h-4 w-4 mr-1.5" /> Preview
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* PREVIEW DIALOG */}
      <Dialog open={!!previewing} onOpenChange={(v) => !v && setPreviewing(null)}>
        <DialogContent className="bg-white max-w-4xl p-0 overflow-hidden">
          {previewing && (
            <div>
              <DialogHeader className="px-6 pt-5 pb-3"><DialogTitle className="font-heading text-base">Hero preview</DialogTitle></DialogHeader>
              <div className="relative h-[380px] bg-slate-900 overflow-hidden" data-testid="hero-preview-canvas">
                {(previewing.image_desktop || previewing.image_mobile) && (
                  <img src={assetUrl(previewing.image_desktop || previewing.image_mobile)} alt={previewing.image_alt || ""} className="absolute inset-0 w-full h-full object-cover" />
                )}
                <div className={`absolute inset-0 ${
                  previewing.overlay_strength === "light" ? "bg-gradient-to-r from-slate-950/65 via-slate-950/35 to-slate-950/10" :
                  previewing.overlay_strength === "strong" ? "bg-gradient-to-r from-slate-950/90 via-slate-950/65 to-slate-950/25" :
                  "bg-gradient-to-r from-slate-950/80 via-slate-950/50 to-slate-950/15"}`} />
                <div className="relative h-full px-8 flex items-center">
                  <div className={`max-w-lg ${previewing.text_align === "center" ? "mx-auto text-center" : previewing.text_align === "right" ? "ml-auto text-right" : ""}`}>
                    <span className="inline-block rounded-full bg-white/10 border border-white/20 px-3 py-1 text-[10px] font-bold tracking-[0.2em] text-white uppercase mb-3">{previewing.label}</span>
                    <h2 className="font-heading font-bold text-3xl text-white leading-tight">{previewing.headline}</h2>
                    {previewing.subheadline && <p className="font-heading font-semibold text-lg text-emerald-300 mt-2">{previewing.subheadline}</p>}
                    {previewing.description && <p className="text-xs text-slate-200 mt-3 leading-relaxed">{previewing.description}</p>}
                    <div className="flex flex-wrap gap-2 mt-4">
                      {(previewing.ctas || []).filter((c) => c.enabled && c.text).map((c, i) => (
                        <span key={i} className={`inline-flex items-center rounded-lg text-xs font-semibold h-8 px-3.5 ${i === 0 ? "bg-white text-slate-900" : i === 1 ? "border border-white/70 text-white" : "text-white underline"}`}>{c.text}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
